/* global window, document */
import * as React from "react";
/**
 * This sets up an timer for automatically closing or hiding a component. It
 * returns functions for starting or stopping the timer in case the component
 * needs control, i.e. stopping the timer on hover.
 */

export function useAutoClose(duration, onClose) {
  var timeoutId = React.useRef();
  var startTimer = React.useCallback(function () {
    if (duration) {
      timeoutId.current = setTimeout(function () {
        onClose();
      }, duration * 1000);
    }
  }, [duration, onClose]);

  function stopTimer() {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = undefined;
    }
  }

  React.useEffect(function () {
    startTimer();
    return function () {
      stopTimer();
    };
  }, [startTimer, duration]);
  return [startTimer, stopTimer];
}
/**
 * Creates an element that is automatically added to the document
 * on mount and removed on unmount.
 */

export function useContainerElement(dataAttr) {
  var container;

  if (typeof window !== "undefined" && typeof window.document !== "undefined") {
    container = document.createElement("div");
    container.setAttribute(dataAttr, "");
  }

  React.useEffect(function () {
    if (document.body && container) {
      document.body.appendChild(container);
    }

    return function () {
      if (document.body && container) {
        document.body.removeChild(container);
      }
    };
  });
  return container;
}
/**
 * Hook for determining if focus is within the given container element. You can
 * also provide `onFocus` and `onHook` handlers to handle when focus is changing.
 */

export function useFocusWithin(_temp) {
  var _ref = _temp === void 0 ? {} : _temp,
      onFocus = _ref.onFocus,
      onBlur = _ref.onBlur;

  var ref = React.useRef(null);

  var _React$useState = React.useState(false),
      focused = _React$useState[0],
      setFocused = _React$useState[1];

  React.useEffect(function () {
    function handler(event) {
      var element = ref.current;
      var target = event.target;
      var nowFocused = element != null && target instanceof HTMLElement && element.contains(target);
      if (nowFocused === focused) return;
      if (nowFocused && onFocus) onFocus(event);
      if (!nowFocused && onBlur) onBlur(event);
      setFocused(nowFocused);
    }

    document.addEventListener("focusin", handler, true);
    return function () {
      document.removeEventListener("focusin", handler, true);
    };
  }, [ref, focused, onFocus, onBlur]);
  return [ref, focused];
}